/**
 * Content Management Service
 * API calls for RLC methodology content management (superuser only)
 */

import { authenticatedGet, authenticatedPost, authenticatedPatch, authenticatedDelete, buildApiUrl } from '../utils/authUtils';

const CONTENT_API_BASE = '/admin/content';

const contentManagementService = {
  /**
   * List all RLC methodology documents
   * @param {Object} filters - Optional filters (category, status_filter)
   * @returns {Promise<Object>} { success, documents, total }
   */
  async listDocuments(filters = {}) {
    const params = new URLSearchParams();
    if (filters.category) {
      params.append('category', filters.category);
    }
    if (filters.status_filter) {
      params.append('status_filter', filters.status_filter);
    }

    const url = buildApiUrl(CONTENT_API_BASE + '/list' + (params.toString() ? '?' + params.toString() : ''));
    return await authenticatedGet(url);
  },

  /**
   * Get details for a specific document
   * @param {string} documentId - Document UUID
   * @returns {Promise<Object>} { success, document }
   */
  async getDocument(documentId) {
    const url = buildApiUrl(`${CONTENT_API_BASE}/${documentId}`);
    return await authenticatedGet(url);
  },

  /**
   * Update document metadata (tags, description, category)
   * @param {string} documentId - Document UUID
   * @param {Object} updateData - Data to update (tags, description, category)
   * @returns {Promise<Object>} { success, document_id, updated_fields }
   */
  async updateDocument(documentId, updateData) {
    const url = buildApiUrl(`${CONTENT_API_BASE}/${documentId}`);
    return await authenticatedPatch(url, updateData);
  },

  /**
   * Upload a new RLC methodology document
   * @param {FormData} formData - FormData with file, title, folder_path, and optional description
   * @returns {Promise<Object>} { success, document, message }
   */
  async uploadDocument(formData) {
    const url = buildApiUrl(`${CONTENT_API_BASE}/upload`);

    // Use fetch directly since we're sending FormData
    // Get token from user object (stored in localStorage as 'user', not 'token')
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      throw new Error('Not authenticated. Please log in again.');
    }
    const user = JSON.parse(userStr);
    const token = user.access_token;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Upload failed');
    }

    return await response.json();
  },

  /**
   * Batch delete multiple documents
   * @param {Array<string>} documentIds - Array of document UUIDs
   * @returns {Promise<Object>} { success, total, successful, failed, errors, message }
   */
  async batchDeleteDocuments(documentIds) {
    const url = buildApiUrl(`${CONTENT_API_BASE}/batch-delete`);
    return await authenticatedPost(url, { document_ids: documentIds });
  },

  /**
   * Delete a folder and all documents within it
   * @param {string} folderPath - Folder path (e.g., "/Books")
   * @returns {Promise<Object>} { success, folder_path, documents_deleted, documents_failed, errors, message }
   */
  async deleteFolder(folderPath) {
    const url = buildApiUrl(`${CONTENT_API_BASE}/delete-folder`);
    return await authenticatedPost(url, { folder_path: folderPath });
  },

  /**
   * Get presigned URL to download document source file
   * @param {string} documentId - Document UUID
   * @returns {Promise<Object>} { success, document_id, filename, download_url, expires_in }
   */
  async getDocumentSource(documentId) {
    const url = buildApiUrl(`${CONTENT_API_BASE}/${documentId}/source`);
    return await authenticatedGet(url);
  },

  /**
   * Delete a document and all its embeddings
   * @param {string} documentId - Document UUID
   * @returns {Promise<Object>} { success, document_id, title, embeddings_deleted, message }
   */
  async deleteDocument(documentId) {
    const url = buildApiUrl(`${CONTENT_API_BASE}/${documentId}`);
    return await authenticatedDelete(url);
  },

  /**
   * Reset a stuck lock on a document
   * @param {string} documentId - Document UUID
   * @returns {Promise<Object>} { success, document_id, message }
   */
  async resetDocumentLock(documentId) {
    const url = buildApiUrl(`${CONTENT_API_BASE}/${documentId}/reset-lock`);
    return await authenticatedPost(url, {});
  },

  /**
   * Run stale lock cleanup job manually
   * @returns {Promise<Object>} { success, locks_reset, message }
   */
  async cleanupStaleLocks() {
    const url = buildApiUrl(`${CONTENT_API_BASE}/cleanup-stale-locks`);
    return await authenticatedPost(url, {});
  },

  /**
   * Get all unique tags across all documents
   * @returns {Promise<Object>} { success, tags, total }
   */
  async getAllTags() {
    const url = buildApiUrl(`${CONTENT_API_BASE}/tags`);
    return await authenticatedGet(url);
  },
};

export default contentManagementService;
